import { Collaborator } from '@/models';

/**
 * Tipos de status possíveis para colaboradores
 */
export type StatusType = 'PENDING' | 'IN_PROGRESS' | 'UPDATED';

/**
 * Normaliza um valor de status, tratando valores falsy como PENDING
 */
export const normalizeStatus = (status: string | null | undefined): StatusType => {
    if (!status || status.trim() === '') {
        return 'PENDING';
    }

    const upperStatus = status.toUpperCase();
    if (upperStatus === 'IN_PROGRESS' || upperStatus === 'UPDATED') {
        return upperStatus as StatusType;
    }

    return 'PENDING';
};

/**
 * Calcula o status consolidado baseado nos 3 status individuais (DHO, DP, Tech)
 * 
 * Lógica:
 * - Se ao menos um dos 3 status for PENDING → PENDING
 * - Se nenhum status for PENDING e ao menos um for IN_PROGRESS → IN_PROGRESS
 * - Se nenhum status for PENDING ou IN_PROGRESS → UPDATED
 */
export const calculateConsolidatedStatus = (collaborator: Collaborator): StatusType => {
    const userStatus = normalizeStatus(collaborator.updatedStatus?.byUser?.status);
    const dhoStatus = normalizeStatus(collaborator.updatedStatus?.byDHO?.status);
    const dpStatus = normalizeStatus(collaborator.updatedStatus?.byDP?.status);
    const techStatus = normalizeStatus(collaborator.updatedStatus?.byTech?.status);

    const statuses = [userStatus, dhoStatus, dpStatus, techStatus];

    // Se ao menos um for PENDING
    if (statuses.includes('PENDING')) {
        return 'PENDING';
    }

    // Se nenhum for PENDING e ao menos um for IN_PROGRESS
    if (statuses.includes('IN_PROGRESS')) {
        return 'IN_PROGRESS';
    }

    // Se todos forem UPDATED
    return 'UPDATED';
};

/**
 * Extrai o status individual de um colaborador baseado no tipo (DHO, DP, Tech)
 */
export const getIndividualStatus = (
    collaborator: Collaborator,
    type: 'byDHO' | 'byDP' | 'byTech'
): StatusType => {
    return normalizeStatus(collaborator.updatedStatus?.[type]?.status);
};

/**
 * Tipos de status de atividade possíveis para colaboradores
 */
export type ActivityStatusType = 'active' | 'inactive';

/**
 * Traduz status de atividade de inglês para português
 */
export const translateActivityStatus = (status: string | null | undefined): string => {
    if (!status) return '-';
    
    const lowerStatus = status.toLowerCase();
    switch (lowerStatus) {
        case 'active':
            return 'Ativo';
        case 'inactive':
            return 'Inativo';
        case 'desligado':
            return 'Desligado';
        case 'afastado':
            return 'Afastado';
        default:
            return status; // Retorna o valor original se não reconhecer
    }
};

/**
 * Normaliza o valor do status para o formato correto do dropdown
 */
export const normalizeActivityStatus = (status: string | null | undefined): string => {
    if (!status) return '';
    
    const lowerStatus = status.toLowerCase();
    switch (lowerStatus) {
        case 'active':
            return 'active';
        case 'inactive':
            return 'inactive';
        case 'desligado':
            return 'desligado';
        case 'afastado':
            return 'afastado';
        default:
            return lowerStatus;
    }
};

/**
 * Opções para select de status de atividade
 */
export const activityStatusOptions = [
    { value: 'active', label: 'Ativo' },
    { value: 'inactive', label: 'Inativo' },
    { value: 'desligado', label: 'Desligado' },
    { value: 'afastado', label: 'Afastado' }
]; 